// MIDAS V3 — Response card (Phase 2)
//
// Ports `PinolaCard` structure from the design handoff:
//   ToDo/MidasChat/Claude_Design_files/machineid/design_handoff_midas_v3/
//   chat-window.jsx (lines 265-358)
//
// Card structure (spec §5):
//   1. Title row (padding 14×18, bottom border): 22×22 accent-soft tile + ⚡
//      glyph + bold 14.5 title. No right-side count.
//   2. Body — children passthrough. v3 reskins, doesn't rewrite, the markdown
//      rendering that v2 already does. ChatBotMessageV3 will mount its
//      markdown output here.
//   3. Warning callout (optional) — amber-soft callout for "open service
//      report on this subsystem". Used when the v2 bot SSE produced an
//      `srCitations` entry that resolves to an open SR.
//   4. Follow-ups tray (optional) — `var(--card-2)` bg + "Would you also
//      like" eyebrow + accent-bordered pill chips. Maps from v2 message
//      `suggestions` field.
//
// Wrap-no-restyle invariant: this card is a CHROME. The markdown content
// passed as children renders with its own v3-themed table/list/code/cite
// styling (handled by ChatBotMessageV3). The card does not introspect or
// re-style the children.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import ResponseSourcesRowV3 from "./ResponseSourcesRowV3";
import type { SrcRefPayload } from "./srcChipsGrammar";

export interface ResponseCardWarning {
  /** Bold first line — e.g. "Open service report on this subsystem". */
  title: string;
  /** Mono subline — e.g. "SR-260326-001 · Nefunguje Pinola · opened 26.03.2026". */
  subtitle?: string;
  /** Button label (defaults to "Open ↗"). */
  buttonLabel?: string;
  /** Click handler for the right-aligned button. */
  onButtonClick?: () => void;
}

export interface ResponseCardFollowUp {
  /** Chip label. */
  label: string;
  /** Click handler (typically resends as a follow-up suggestion). */
  onClick?: () => void;
}

/**
 * A single entry in the consolidated "Sources" footer.
 *
 * The footer renders below the body and any warning, and above the follow-ups
 * tray. Each row is keyboard-focusable: clicking it dispatches the same
 * artifact-open flow as an inline `<CiteV3>` chip.
 *
 * 2026-05-18 follow-up to MIDAS-V3-P2: user expected a consolidated source
 * list under the response (mirrors the bot's prose "Zdroje:" convention),
 * not just the inline citation pills.
 */
export interface ResponseCardSource {
  /** Mono pill text — e.g. "Sheet 020". Required, drives the badge in the
   *  source card's text strip. */
  label: string;
  /** Page number (canonical join key). Passed through to onClick verbatim. */
  page: number;
  /** Localised name shown below the pill — e.g. "DC 24V/10A Power Supply".
   *  Optional. */
  name?: string;
  /** Relative bot-image proxy path (e.g. "/api/v1/image/<doc>/...page_047.jpg")
   *  populated by ChatBotMessageV3 from the matching `message.sources[i]`.
   *  When undefined, the card renders without a thumbnail well. */
  imageUrl?: string;
  /** Click handler. Receives the entry's `page`. */
  onClick?: (page: number) => void;
}

export interface ResponseCardV3Props {
  /** Title text — e.g. "Quill / Tailstock (Pinola) — Connections". */
  title?: string;
  /** Main body (markdown rendered by ChatBotMessageV3). */
  children: React.ReactNode;
  /** Optional amber warning callout shown between body and follow-ups. */
  warning?: ResponseCardWarning;
  /** Optional consolidated sources footer — renders BELOW body+warning, ABOVE
   *  follow-ups. Each row pairs a mono `List NNN` chip with the localised
   *  page name. Pass undefined or [] to hide the section. */
  sources?: ResponseCardSource[];
  /** MIDAS-ANSWER-FORMAT-1 r5: <src-ref> pills hoisted out of the prose by
   *  ChatBotMessageV3 — rendered in the same sources row, after the cards
   *  (logic, then live). Pass undefined or [] when the answer has none. */
  srcRefs?: SrcRefPayload[];
  /** Optional follow-up chip tray rendered at the bottom of the card. */
  followUps?: ResponseCardFollowUp[];
  /** SP-1V (Creative Designer, 2026-07-12): chrome-less Claude-style flow.
   *  When true, the card drops ALL wrapper chrome — no card fill, border,
   *  radius, shadow, or title row — so the assistant turn flows directly on
   *  the page background (matching the §1A reference images). Body, sources,
   *  warning callout, and follow-ups keep their structure but lose the card-
   *  relative gutters so they align flush with the streamed prose column (no
   *  visual pop at the streaming → final handoff). The ONLY consumer is the
   *  v3 assistant turn (ChatBotMessageV3), so this is scoped to that surface;
   *  omit the prop (carded default) for any future reuse. */
  flat?: boolean;
}

const ResponseCardV3: React.FC<ResponseCardV3Props> = ({
  title,
  children,
  warning,
  sources,
  srcRefs,
  followUps,
  flat = false,
}) => {
  const { translate } = useTranslation("chatBot");
  const hasFollowUps = !!followUps && followUps.length > 0;
  const hasWarning = !!warning;
  // In the chrome-less (flat) variant the assistant header strip above the
  // card already carries the "Midas" identity — a card title here would be a
  // redundant second label and the reference shows none, so suppress it.
  const hasTitle = !!title && !flat;
  const hasSources = !!sources && sources.length > 0;
  const hasSrcRefs = !!srcRefs && srcRefs.length > 0;

  return (
    <div
      style={
        flat
          ? {
              fontFamily: "var(--font-sans)",
              color: "var(--ink)",
            }
          : {
              background: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "var(--shadow)",
              fontFamily: "var(--font-sans)",
              color: "var(--ink)",
            }
      }
    >
      {hasTitle && (
        // R3 sub-task 4a: dropped the 22×22 accent-soft tile + ⚡ glyph from
        // the title row. Title text alone is the cleaner header. No callers
        // pass `titleIcon` (verified by grep across src/), so the prop was
        // removed from the public surface as well.
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--border-soft)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 14.5,
              color: "var(--ink)",
              letterSpacing: "-0.005em",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
        </div>
      )}

      {/* Body — passthrough region. v3 markdown renderer mounts here.
          Flat: horizontal padding matches StreamingMarkdownV3's "0 2px" so the
          prose column does NOT shift when the stream settles into the final
          message. */}
      <div
        style={{
          padding: flat ? "0 2px" : "14px 18px",
          fontSize: 14,
          lineHeight: 1.5,
          color: "var(--ink)",
        }}
      >
        {children}
      </div>

      {(hasSources || hasSrcRefs) && (
        // MIDAS-V3-BUGS-0519 sub-task 5 — v2's source-cards strip (thumbnail
        // + label badge + page name, wrapping flex row), now inside the
        // "ZDROJE" sources row together with the <src-ref> pills
        // (MIDAS-ANSWER-FORMAT-1 r5, Paper "Variant E"). See
        // ResponseSourcesRowV3 for the layout values.
        <ResponseSourcesRowV3 sources={sources} srcRefs={srcRefs} flat={flat} />
      )}

      {hasWarning && (
        <div
          style={{
            // Flat: drop the card-relative side gutter; the amber callout keeps
            // its own fill/border so it still reads as an alert on the page bg.
            margin: flat ? "6px 0 12px" : "0 18px 14px",
            padding: "10px 12px",
            // The handoff uses color-mix for the warning fill. We approximate
            // with a fixed-opacity rgba pulled from --amber so it works in
            // browsers without OKLAB color-mix support. The visual effect is
            // the same warm-yellow tint on top of the card.
            background:
              "color-mix(in oklab, var(--amber) 12%, var(--card))",
            border:
              "1px solid color-mix(in oklab, var(--amber) 38%, var(--border))",
            borderRadius: 8,
            display: "flex",
            gap: 10,
            alignItems: "center",
            fontSize: 13,
          }}
        >
          <span
            aria-hidden="true"
            style={{ color: "var(--amber)", flexShrink: 0, fontSize: 14 }}
          >
            ⚠
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: "var(--ink)" }}>
              {warning.title}
            </div>
            {warning.subtitle && (
              <div
                className="mono"
                style={{
                  color: "var(--muted)",
                  fontSize: 12,
                  marginTop: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {warning.subtitle}
              </div>
            )}
          </div>
          {warning.onButtonClick && (
            <button
              type="button"
              onClick={warning.onButtonClick}
              style={{
                height: 26,
                padding: "0 10px",
                fontSize: 12,
                fontFamily: "var(--font-sans)",
                background: "var(--card)",
                color: "var(--ink-2)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {warning.buttonLabel ??
                (translate("midas.v3.responseCard.openSrButton", {
                  defaultValue: "Open ↗",
                }) as string)}
            </button>
          )}
        </div>
      )}

      {hasFollowUps && (
        <div
          style={{
            // Flat: the follow-ups become a plain chip row under the flowing
            // turn — no card-2 tray fill, just a quiet top hairline. Carded
            // keeps the original inset tray.
            padding: flat ? "12px 0 2px" : "10px 14px",
            borderTop: "1px solid var(--border-soft)",
            background: flat ? "transparent" : "var(--card-2)",
            display: "flex",
            gap: 6,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <span
            className="mono"
            style={{
              fontSize: 10,
              letterSpacing: "0.1em",
              color: "var(--faint)",
              textTransform: "uppercase",
              marginRight: 4,
            }}
          >
            {translate("midas.v3.responseCard.followUpsEyebrow", {
              defaultValue: "Would you also like",
            }) as string}
          </span>
          {followUps!.map((chip, i) => (
            <button
              key={`${chip.label}-${i}`}
              type="button"
              onClick={chip.onClick}
              style={{
                height: 24,
                padding: "0 9px",
                fontSize: 11.5,
                borderRadius: 999,
                fontFamily: "var(--font-sans)",
                background: "var(--card)",
                color: "var(--ink-2)",
                border: "1px solid var(--border)",
                cursor: chip.onClick ? "pointer" : "default",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResponseCardV3;
